import { Printer } from 'lucide-react';
import { useEffect, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { BRAND, paletteVars } from '@/features/marketing/brand.config';
import { Brand, wrap } from '@/features/marketing/kit';
import '@/features/marketing/landing.css';
import { cn } from '@/lib/utils';
import { COMPANY, formatLegalDate, LEGAL_PATHS, LEGAL_UPDATED_AT, LEGAL_VERSION } from './legal.config';
import { PRIVACY, TERMS, type LegalBlock, type LegalDoc } from './legal.content';

function useDocumentTitle(title: string) {
  useEffect(() => {
    const prev = document.title;
    document.title = `${title} | BarbeiroPro`;
    return () => {
      document.title = prev;
    };
  }, [title]);
}

function Block({ b }: { b: LegalBlock }) {
  if (b.kind === 'h3') return <h3 className="mt-6 text-lg font-bold">{b.text}</h3>;
  if (b.kind === 'ul')
    return (
      <ul className="mt-3 list-disc space-y-2 pl-6 marker:text-[var(--lp-accent)]">
        {b.items.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    );
  return <p className="mt-3">{b.text}</p>;
}

/** Cabeçalho + conteúdo + rodapé, no estilo da landing (tema da marca), sem o layout autenticado. */
export function LegalPageView({ doc, current }: { doc: LegalDoc; current: 'privacy' | 'terms' }) {
  useDocumentTitle(doc.title);
  return (
    <div className="lp-root min-h-dvh" data-lp-theme={BRAND.palette.theme} style={paletteVars(BRAND.palette) as CSSProperties}>
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-xl focus:bg-[var(--lp-cta)] focus:px-4 focus:py-3 focus:text-[var(--lp-cta-ink)] print:hidden"
      >
        Ir para o conteúdo
      </a>
      <header className="lp-header sticky top-0 z-40 print:static">
        <div className={cn(wrap, 'flex h-[4.5rem] items-center justify-between gap-4')}>
          <Link to={BRAND.routes.home} className="lp-link rounded-xl" aria-label={BRAND.homeLabel}>
            <Brand />
          </Link>
          <nav aria-label="Documentos" className="flex items-center gap-1 print:hidden">
            <Link
              to={LEGAL_PATHS.terms}
              aria-current={current === 'terms' ? 'page' : undefined}
              className="lp-navlink inline-flex min-h-11 items-center rounded-xl px-3 text-sm font-medium aria-[current=page]:font-bold aria-[current=page]:underline aria-[current=page]:underline-offset-8"
            >
              Termos de Uso
            </Link>
            <Link
              to={LEGAL_PATHS.privacy}
              aria-current={current === 'privacy' ? 'page' : undefined}
              className="lp-navlink inline-flex min-h-11 items-center rounded-xl px-3 text-sm font-medium aria-[current=page]:font-bold aria-[current=page]:underline aria-[current=page]:underline-offset-8"
            >
              Privacidade
            </Link>
          </nav>
        </div>
      </header>

      <main id="conteudo" className="pb-20 pt-10 sm:pt-14">
        <div className={cn(wrap, 'grid gap-10 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)] lg:gap-16')}>
          <aside className="print:hidden lg:sticky lg:top-28 lg:self-start">
            <nav aria-label="Índice" className="lp-card rounded-2xl p-4">
              <p className="px-2 pb-2 text-sm font-bold">Neste documento</p>
              <ol className="grid gap-0.5 text-sm">
                {doc.sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="lp-navlink inline-flex min-h-10 w-full items-center rounded-lg px-2 leading-snug">
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          <article className="min-w-0 max-w-[46rem]">
            <h1 className="lp-h2">{doc.title}</h1>
            <p className="lp-muted mt-3 text-sm" data-testid="legal-version">
              Versão {LEGAL_VERSION} · atualizada em {formatLegalDate(LEGAL_UPDATED_AT)}
            </p>
            <button
              type="button"
              onClick={() => window.print()}
              className="lp-navlink mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-[var(--lp-line)] px-4 text-sm font-medium print:hidden"
            >
              <Printer className="size-4" aria-hidden /> Imprimir
            </button>
            <p className="lp-lead mt-6">{doc.intro}</p>

            {doc.sections.map((s) => (
              <section key={s.id} id={s.id} aria-labelledby={`${s.id}-t`} className="scroll-mt-28 pt-10 text-base leading-relaxed">
                <h2 id={`${s.id}-t`} className="text-2xl font-extrabold tracking-tight">
                  {s.title}
                </h2>
                {s.blocks.map((b, i) => (
                  <Block key={i} b={b} />
                ))}
              </section>
            ))}
          </article>
        </div>
      </main>

      <footer className="lp-footer py-10 print:hidden">
        <div className={cn(wrap, 'flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between')}>
          <p className="lp-muted">
            {COMPANY.name} · <a className="lp-navlink underline-offset-4 hover:underline" href={`mailto:${COMPANY.contactEmail}`}>{COMPANY.contactEmail}</a>
          </p>
          <nav aria-label="Rodapé" className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to={BRAND.routes.home} className="lp-navlink inline-flex min-h-11 items-center">
              Início
            </Link>
            <Link to={BRAND.routes.login} className="lp-navlink inline-flex min-h-11 items-center">
              {BRAND.cta.login}
            </Link>
            <Link to={BRAND.routes.signup} className="lp-navlink inline-flex min-h-11 items-center">
              {BRAND.cta.signup}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

export const PrivacyPage = () => <LegalPageView doc={PRIVACY} current="privacy" />;
export const TermsPage = () => <LegalPageView doc={TERMS} current="terms" />;
